import React, { useEffect, useState } from "react";
import { api, getToken } from "@/lib/api";
import { X, Loader2, Trash2, Upload, Search, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * APNGLibraryPicker — modal for browsing / uploading tenant-scoped APNG assets.
 *
 * Props:
 *   open       : boolean
 *   onClose()  : close the modal
 *   onSelect(asset) : called when the operator picks an asset. `asset` is
 *                    {id, name, image_url, is_animated, tags, ...}.
 */
export default function APNGLibraryPicker({ open, onClose, onSelect }) {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tagFilter, setTagFilter] = useState("");
  const [search, setSearch] = useState("");
  const [uploading, setUploading] = useState(false);
  const [storageReady, setStorageReady] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      setLoading(true);
      try {
        const [assetsRes, statusRes] = await Promise.all([
          api.get("/apng-library"),
          api.get("/apng-library/storage-status").catch(() => ({ data: { available: false } })),
        ]);
        // Storage-backed assets need `?auth=<jwt>` on the image URL because
        // <img src> cannot send Authorization headers.
        const token = getToken() || "";
        const enriched = (assetsRes.data || []).map((a) => {
          if (a.storage_path && a.image_url && a.image_url.startsWith("/api/")) {
            return { ...a, image_url: `${a.image_url}?auth=${encodeURIComponent(token)}` };
          }
          return a;
        });
        setAssets(enriched);
        setStorageReady(!!statusRes.data?.available);
      } catch (e) {
        toast.error("Failed to load APNG library");
      } finally {
        setLoading(false);
      }
    })();
  }, [open]);

  if (!open) return null;

  const filtered = assets.filter((a) => {
    if (tagFilter && !(a.tags || []).includes(tagFilter)) return false;
    if (search && !a.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const allTags = Array.from(new Set(assets.flatMap((a) => a.tags || []))).slice(0, 20);

  async function onFileUpload(file) {
    if (!file) return;
    // 5 MB cap on the real-storage path; fall back to data-URL for tiny files
    // if the storage backend is unavailable.
    const MAX = 5_000_000;
    if (file.size > MAX) {
      toast.error(`File is ${(file.size / 1024 / 1024).toFixed(1)} MB — max is 5 MB`);
      return;
    }
    setUploading(true);
    try {
      if (storageReady) {
        // Real multipart upload → Emergent Object Storage
        const form = new FormData();
        form.append("file", file);
        form.append("name", file.name.replace(/\.(png|apng|webp|gif)$/i, ""));
        const r = await api.post("/apng-library/upload", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        // Rewrite image_url to include the auth query so <img> can render it
        const token = getToken() || "";
        const asset = {
          ...r.data,
          image_url: `${r.data.image_url}?auth=${encodeURIComponent(token)}`,
        };
        setAssets((prev) => [asset, ...prev]);
        toast.success(`Uploaded · ${asset.name}`);
      } else {
        // Legacy path — inline data URL, capped at 1 MB
        if (file.size > 1_000_000) {
          toast.error("Object storage unavailable — use a URL for files over 1 MB");
          return;
        }
        const dataUrl = await new Promise((resolve, reject) => {
          const fr = new FileReader();
          fr.onload = () => resolve(fr.result);
          fr.onerror = reject;
          fr.readAsDataURL(file);
        });
        const buf = await file.slice(0, 2048).arrayBuffer();
        const bytes = new Uint8Array(buf);
        let animated = false;
        for (let i = 8; i < bytes.length - 4; i++) {
          if (bytes[i] === 0x61 && bytes[i + 1] === 0x63 && bytes[i + 2] === 0x54 && bytes[i + 3] === 0x4c) {
            animated = true;
            break;
          }
        }
        const nameGuess = file.name.replace(/\.png$/i, "");
        const r = await api.post("/apng-library", {
          name: nameGuess,
          image_url: dataUrl,
          is_animated: animated,
          file_name: file.name,
          file_size_bytes: file.size,
          tags: animated ? ["animated"] : ["static"],
        });
        setAssets((prev) => [r.data, ...prev]);
        toast.success(`Uploaded · ${nameGuess}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  }

  async function onDelete(id) {
    try {
      await api.delete(`/apng-library/${id}`);
      setAssets((prev) => prev.filter((a) => a.id !== id));
    } catch {
      toast.error("Delete failed");
    }
  }

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      data-testid="apng-library-backdrop"
      onClick={onClose}
    >
      <div
        className="w-full max-w-3xl h-[75vh] rounded-sm border border-zinc-800 bg-[#0b0b0e] shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="apng-library-modal"
      >
        <div className="flex items-start justify-between p-4 border-b border-zinc-800">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-400">
              § APNG Library
            </div>
            <div className="font-display font-bold text-lg mt-0.5">Choose a channel bug, intro or lower third</div>
            <div className="text-xs text-zinc-500 mt-0.5">Upload once, drop into any channel&apos;s 14-layer graphics editor.</div>
          </div>
          <button
            onClick={onClose}
            data-testid="apng-library-close"
            aria-label="Close"
            className="text-zinc-500 hover:text-zinc-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex items-center gap-2 px-4 py-2 border-b border-zinc-900 bg-black/30">
          <div className="relative flex-1">
            <Search className="w-3 h-3 absolute left-2 top-2 text-zinc-500" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search…"
              data-testid="apng-library-search"
              className="w-full bg-zinc-900 border border-zinc-800 rounded-sm px-6 py-1 text-xs font-mono outline-none focus:border-emerald-500/60"
            />
          </div>
          <select
            value={tagFilter}
            onChange={(e) => setTagFilter(e.target.value)}
            data-testid="apng-library-tag-filter"
            className="bg-zinc-900 border border-zinc-800 rounded-sm text-xs px-2 py-1 font-mono"
          >
            <option value="">All tags</option>
            {allTags.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <label className="tenant-btn font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-sm cursor-pointer flex items-center gap-1.5" data-testid="apng-library-upload-label">
            {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
            Upload
            <input
              type="file"
              accept=".png,image/png,image/apng"
              className="hidden"
              onChange={(e) => onFileUpload(e.target.files?.[0])}
              data-testid="apng-library-upload-input"
            />
          </label>
        </div>

        <div className="flex-1 overflow-y-auto p-4" data-testid="apng-library-grid">
          {loading && (
            <div className="h-full flex items-center justify-center text-zinc-500 font-mono text-xs">
              <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading library…
            </div>
          )}
          {!loading && filtered.length === 0 && (
            <div className="h-full flex flex-col items-center justify-center text-zinc-500 font-mono text-xs" data-testid="apng-library-empty">
              <div>No APNG assets yet.</div>
              <div className="text-[10px] mt-1">Click <span className="text-emerald-400">Upload</span> above to add your first channel bug.</div>
            </div>
          )}
          {!loading && filtered.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
              {filtered.map((a) => (
                <div
                  key={a.id}
                  data-testid={`apng-library-asset-${a.id}`}
                  className="group border border-zinc-800 rounded-sm bg-black overflow-hidden flex flex-col"
                >
                  <div className="aspect-video flex items-center justify-center bg-zinc-950 border-b border-zinc-800 overflow-hidden">
                    <img
                      src={a.image_url}
                      alt={a.name}
                      className="max-h-full max-w-full object-contain"
                    />
                  </div>
                  <div className="p-2 space-y-1">
                    <div className="text-xs font-display font-bold truncate" title={a.name}>{a.name}</div>
                    <div className="flex items-center gap-1 flex-wrap">
                      {(a.tags || []).slice(0, 3).map((t) => (
                        <span key={t} className="text-[9px] font-mono uppercase tracking-widest text-emerald-400 border border-emerald-500/40 rounded-sm px-1">{t}</span>
                      ))}
                      {a.is_animated ? (
                        <span className="text-[9px] font-mono uppercase text-amber-400">APNG</span>
                      ) : (
                        <span className="text-[9px] font-mono uppercase text-zinc-600">static</span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 pt-1">
                      <button
                        onClick={() => { onSelect(a); onClose(); }}
                        data-testid={`apng-library-pick-${a.id}`}
                        className="flex-1 tenant-btn rounded-sm px-2 py-1 font-mono text-[10px] uppercase tracking-widest flex items-center justify-center gap-1"
                      >
                        <Check className="w-3 h-3" /> Use
                      </button>
                      <button
                        onClick={() => onDelete(a.id)}
                        data-testid={`apng-library-delete-${a.id}`}
                        aria-label={`Delete ${a.name}`}
                        className="rounded-sm border border-zinc-800 hover:border-red-500/60 hover:text-red-400 text-zinc-500 p-1"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
